import type { Ton } from '../commun.js'
import { nomSalle, ordreSalle } from '../salles.js'
import {
  agePlusAncien, bool01, estVueDomaine, objet,
  type AppareilBref, type Collecteur, type Generique, type SourceDomaine,
} from './kit-modele.js'

/**
 * VIDÉOSURVEILLANCE — modèle pur de `GET /v1/video` (21/10/2026).
 *
 * Deux lectures se complètent :
 *  · le REGISTRE de Sonde (`cameras`, `nvrs[].channels`) : ce qui est
 *    rattaché à un poste. Le 21/10 il est vide (caméras « À valider ») ;
 *  · les MESURES des familles (`generic`, `hikvision.*`, `protect.*`) : ce
 *    que les NVR et Protect disent d'eux-mêmes, même sans poste au registre.
 *
 * Règles : un NVR plein à 100 % qui écrase ses plus anciennes images est
 * NORMAL (ce qui compte est la rétention effective) ; une source NVR
 * indisponible est grise, jamais « hors ligne » ; une voie qui n'enregistre
 * plus passe en tête. Aucun conseil de renommage de voie.
 */

export interface VoieRegistre {
  channel: number
  online: boolean | null
  recording: boolean | null
  record_status: number | null
  bitrate_kbps: number | null
  video_loss: boolean | null
  camera: { uid: string; display_name: string; display_state: string } | null
  at: string | null
  data_age_s: number | null
}

export interface NvrRegistre {
  device: AppareilBref
  free_ram_mb: number | null
  resp_ms: number | null
  channels: VoieRegistre[]
}

export interface FluxVideo {
  path: string
  uid: string | null
  ready: boolean | null
  readers: number | null
  tracks: number | null
  bytes_in_bps: number | null
  bytes_out_bps: number | null
  frames_err_5m: number | null
  on_demand: boolean | null
  at: string | null
  data_age_s: number | null
}

export interface VideoDoc {
  generated_at: string
  collector: Collecteur
  sources: SourceDomaine[]
  counts: { cameras: number; online: number; offline: number; unobserved: number; channels: number; channels_recording: number }
  cameras: AppareilBref[]
  nvrs: NvrRegistre[]
  streams: FluxVideo[]
  generic: Generique[]
}

export function estVideoDoc(x: unknown): x is VideoDoc {
  return estVueDomaine(x, ['cameras', 'nvrs', 'streams', 'generic']) && objet(x['counts'])
}

/* ═══════════════ Ce que les NVR Hikvision disent d'eux-mêmes ═══════════════ */

export interface VoieMesuree { voie: number; continu: boolean | null; planning: boolean | null; retention_j: number | null; age_s: number | null }

export interface DisqueMesure {
  disque: string
  statut: number | null
  capacite_go: number | null
  smart_ok: boolean | null
  temp_c: number | null
  realloues: number | null
  en_attente: number | null
  non_corrigibles: number | null
  jours: number | null
  age_s: number | null
}

export interface NvrMesure {
  /** Clé de la famille hikvision (« nvr-001 ») : préfixe des voies `<clé>:D<n>`. */
  cle: string
  voies: VoieMesuree[]
  disques: DisqueMesure[]
  /** Rétention effective : celle du NVR, sinon la plus courte de ses voies. */
  retention_j: number | null
  couverture_pct: number | null
  horloge_s: number | null
  debit_mbps: number | null
  age_s: number | null
}

const VOIE = /^([a-z0-9][a-z0-9_-]{0,40}):D(\d{1,3})$/i
const DISQUE = /^([a-z0-9][a-z0-9_-]{0,40}):(hdd\d{1,3})$/i
const NVR = /^hik_nvr:([a-z0-9][a-z0-9_-]{0,40})$/i

export function nvrsMesures(generic: Generique[]): NvrMesure[] {
  const parCle = new Map<string, { voies: Map<number, VoieMesuree>; disques: Map<string, DisqueMesure>; nvr: Generique[] }>()
  const de = (cle: string) => {
    const k = cle.toLowerCase()
    let v = parCle.get(k)
    if (!v) { v = { voies: new Map(), disques: new Map(), nvr: [] }; parCle.set(k, v) }
    return v
  }
  for (const g of generic) {
    if (!g.metric.startsWith('hikvision.')) continue
    let m = VOIE.exec(g.entity_key)
    if (m) {
      const n = Number(m[2])
      const bloc = de(m[1]!)
      const v = bloc.voies.get(n) ?? { voie: n, continu: null, planning: null, retention_j: null, age_s: null }
      if (g.metric === 'hikvision.continuous_recording') v.continu = bool01(g.value)
      else if (g.metric === 'hikvision.schedule_enabled') v.planning = bool01(g.value)
      else if (g.metric === 'hikvision.retention_days') v.retention_j = g.value
      else continue
      v.age_s = agePlusAncien([v.age_s, g.data_age_s])
      bloc.voies.set(n, v)
      continue
    }
    m = DISQUE.exec(g.entity_key)
    if (m) {
      const bloc = de(m[1]!)
      const id = m[2]!.toLowerCase()
      const d = bloc.disques.get(id) ?? {
        disque: id, statut: null, capacite_go: null, smart_ok: null, temp_c: null, realloues: null, en_attente: null, non_corrigibles: null, jours: null, age_s: null,
      }
      switch (g.metric) {
        case 'hikvision.hdd_status': d.statut = g.value; break
        case 'hikvision.hdd_capacity_gb': d.capacite_go = g.value; break
        case 'hikvision.hdd_smart_ok': d.smart_ok = bool01(g.value); break
        case 'hikvision.hdd_temp_c': d.temp_c = g.value; break
        case 'hikvision.hdd_reallocated_sectors': d.realloues = g.value; break
        case 'hikvision.hdd_pending_sectors': d.en_attente = g.value; break
        case 'hikvision.hdd_uncorrectable_sectors': d.non_corrigibles = g.value; break
        case 'hikvision.hdd_power_on_days': d.jours = g.value; break
        default: continue
      }
      d.age_s = agePlusAncien([d.age_s, g.data_age_s])
      bloc.disques.set(id, d)
      continue
    }
    m = NVR.exec(g.entity_key)
    if (m) de(m[1]!).nvr.push(g)
  }
  return [...parCle.entries()].map(([cle, b]) => {
    const val = (metric: string) => b.nvr.find((g) => g.metric === metric)?.value ?? null
    const voies = [...b.voies.values()].sort((a, z) => a.voie - z.voie)
    const disques = [...b.disques.values()].sort((a, z) => a.disque.localeCompare(z.disque, 'fr', { numeric: true }))
    const parVoie = voies.map((v) => v.retention_j).filter((x): x is number => typeof x === 'number')
    const retention = val('hikvision.retention_days') ?? (parVoie.length ? Math.min(...parVoie) : null)
    return {
      cle, voies, disques,
      retention_j: retention,
      couverture_pct: val('hikvision.retention_coverage_pct'),
      horloge_s: val('hikvision.clock_offset_s'),
      debit_mbps: val('hikvision.declared_input_mbps'),
      age_s: agePlusAncien([...b.nvr.map((g) => g.data_age_s), ...voies.map((v) => v.age_s), ...disques.map((d) => d.age_s)]),
    }
  }).sort((a, z) => a.cle.localeCompare(z.cle, 'fr', { numeric: true }))
}

/** La source de Sonde d'un NVR Hikvision : son libellé commence par la clé (« NVR-001 Hikvision … »). */
export function sourceDuNvr(cle: string, sources: SourceDomaine[]): SourceDomaine | null {
  const k = cle.toUpperCase()
  return sources.find((s) => s.code.startsWith('hikvision_nvr') && s.label.toUpperCase().startsWith(k)) ?? null
}

/** État d'un disque de NVR (0 en service, 1 en veille, 2 anomalie). Un disque en veille est normal. */
export function etatDisque(d: DisqueMesure): { ton: Ton; libelle: string } {
  if (d.statut === 2) return { ton: 'err', libelle: 'Anomalie' }
  if (d.smart_ok === false) return { ton: 'warn', libelle: 'SMART dégradé' }
  if ((d.non_corrigibles ?? 0) > 0 || (d.en_attente ?? 0) > 0) return { ton: 'warn', libelle: 'Secteurs à surveiller' }
  if (d.statut === 0) return { ton: 'ok', libelle: 'En service' }
  if (d.statut === 1) return { ton: 'off', libelle: 'En veille (normal)' }
  return { ton: 'gris', libelle: 'État inconnu' }
}

/** Le NVR du registre qui porte cette clé de famille (uid ou nom qui commence par elle). */
export function nvrDuRegistre(cle: string, nvrs: NvrRegistre[]): NvrRegistre | null {
  const k = cle.toLowerCase()
  return nvrs.find((n) => n.device.uid === k)
    ?? nvrs.find((n) => n.device.uid.startsWith(`${k}-`) || n.device.display_name.toLowerCase().startsWith(k))
    ?? null
}

/* ═══════════════ Ce que Protect dit de lui-même ═══════════════ */

export interface ProtectMesure {
  cameras_total: number | null
  cameras_connectees: number | null
  cameras_tierces: number | null
  viewports_total: number | null
  viewports_connectes: number | null
  stockage_pct: number | null
  stockage_utilise_go: number | null
  stockage_total_go: number | null
  ecrasement: boolean | null
  retention_j: number | null
  retention_cible_j: number | null
  capacite_j: number | null
  absents_du_registre: number | null
  age_s: number | null
}

export function protectMesure(generic: Generique[]): ProtectMesure | null {
  // Mesures d'ensemble seulement : la source elle-même ou le NVR Protect (pas les caméras une à une, pas Access).
  const g = generic.filter((x) => x.metric.startsWith('protect.') && (x.entity_key === 'source:unifi_protect' || x.entity_key.startsWith('device:')))
  if (g.length === 0) return null
  const v = (metric: string) => g.find((x) => x.metric === metric)?.value ?? null
  return {
    cameras_total: v('protect.cameras_total'),
    cameras_connectees: v('protect.cameras_connected'),
    cameras_tierces: v('protect.cameras_third_party'),
    viewports_total: v('protect.viewers_total'),
    viewports_connectes: v('protect.viewers_connected'),
    stockage_pct: v('protect.storage_used_pct'),
    stockage_utilise_go: v('protect.storage_used_gb'),
    stockage_total_go: v('protect.storage_size_gb'),
    ecrasement: bool01(v('protect.recycling')),
    retention_j: v('protect.retention_days'),
    retention_cible_j: v('protect.retention_target_days'),
    capacite_j: v('protect.capacity_days'),
    absents_du_registre: v('protect.registry_unmatched'),
    age_s: agePlusAncien(g.map((x) => x.data_age_s)),
  }
}

/**
 * Le stockage d'un enregistreur. Plein + écrasement = NORMAL : l'enregistreur
 * remplace ses plus anciennes images, la rétention effective dit le reste.
 */
export function verdictStockage(pct: number | null, ecrasement: boolean | null): { ton: Ton; texte: string } {
  if (pct === null) return { ton: 'gris', texte: 'Remplissage non lu.' }
  const p = `${Math.round(pct).toLocaleString('fr-FR')} %`
  if (pct >= 95) {
    if (ecrasement === true) return { ton: 'ok', texte: `Plein (${p}) et écrase les plus anciennes images : c’est normal.` }
    if (ecrasement === false) return { ton: 'err', texte: `Plein (${p}) sans écrasement : les nouvelles images ne peuvent plus s’enregistrer.` }
    return { ton: 'gris', texte: `Plein (${p}) ; l’écrasement des anciennes images n’est pas lu.` }
  }
  return { ton: 'ok', texte: `${p} utilisé.` }
}

/* ═══════════════ Voies à voir en premier ═══════════════ */

export interface VoieEnEvidence {
  cle: string
  nvr: string
  voie: number
  camera: { uid: string; nom: string } | null
  ton: Ton
  texte: string
  age_s: number | null
}

/**
 * Les voies qui n'enregistrent plus, en tête. Registre d'abord (enregistrement
 * constaté), mesures ensuite (planning coupé, enregistrement non continu).
 */
export function voiesEnEvidence(nvrs: NvrRegistre[], mesures: NvrMesure[]): VoieEnEvidence[] {
  const out: VoieEnEvidence[] = []
  const vues = new Set<string>()
  for (const n of nvrs) {
    for (const c of n.channels) {
      const camera = c.camera ? { uid: c.camera.uid, nom: c.camera.display_name } : null
      const base = { nvr: n.device.display_name, voie: c.channel, camera, age_s: c.data_age_s }
      if (c.recording === false) {
        out.push({ ...base, cle: `${n.device.uid}:${c.channel}`, ton: 'err', texte: c.video_loss === true ? 'N’enregistre plus : signal vidéo perdu.' : 'N’enregistre plus.' })
      } else if (c.video_loss === true) {
        out.push({ ...base, cle: `${n.device.uid}:${c.channel}`, ton: 'err', texte: 'Signal vidéo perdu.' })
      }
      // Le registre a parlé pour cette voie : les mesures ne la répètent pas.
      if (c.recording !== null || c.video_loss !== null) vues.add(`${n.device.uid}:${c.channel}`)
    }
  }
  for (const m of mesures) {
    const reg = nvrDuRegistre(m.cle, nvrs)
    for (const v of m.voies) {
      if (reg && vues.has(`${reg.device.uid}:${v.voie}`)) continue
      const base = { cle: `${m.cle}:${v.voie}`, nvr: reg?.device.display_name ?? m.cle.toUpperCase(), voie: v.voie, camera: null, age_s: v.age_s }
      if (v.planning === false) out.push({ ...base, ton: 'warn', texte: 'Planning d’enregistrement désactivé : cette voie n’enregistre pas.' })
      else if (v.continu === false) out.push({ ...base, ton: 'warn', texte: 'Enregistrement pas continu 7 j/7.' })
    }
  }
  const rang: Record<Ton, number> = { err: 0, warn: 1, maint: 2, off: 3, gris: 4, ok: 5 }
  return out.sort((a, z) => rang[a.ton] - rang[z.ton] || a.nvr.localeCompare(z.nvr, 'fr', { numeric: true }) || a.voie - z.voie)
}

/** Une voie du registre en couleur : enregistre (vert), n'enregistre plus (rose), inconnu (gris). */
export function tonVoie(c: VoieRegistre): Ton {
  if (c.recording === false || c.video_loss === true) return 'err'
  if (c.recording === true) return 'ok'
  return 'gris'
}

/** Un flux MediaMTX : prêt, au repos à la demande (normal), pas prêt, inconnu. */
export function etatFlux(f: FluxVideo): { ton: Ton; libelle: string } {
  if (f.ready === true) return (f.frames_err_5m ?? 0) > 0 ? { ton: 'warn', libelle: 'Prêt, images en erreur' } : { ton: 'ok', libelle: 'Prêt' }
  if (f.ready === false) return f.on_demand === true ? { ton: 'off', libelle: 'À la demande, au repos' } : { ton: 'warn', libelle: 'Pas prêt' }
  return { ton: 'gris', libelle: 'Inconnu' }
}

/** Caméras du registre rangées par salle (« Sans salle » en dernier). */
export function camerasParSalle(cameras: AppareilBref[], filtre: string): { salle: string; cameras: AppareilBref[] }[] {
  const f = filtre.trim().toLowerCase()
  const garde = f ? cameras.filter((c) => `${c.display_name} ${c.uid} ${c.zone_code ?? ''}`.toLowerCase().includes(f)) : cameras
  const m = new Map<string, AppareilBref[]>()
  for (const c of garde) {
    const k = c.zone_code ?? ''
    m.set(k, [...(m.get(k) ?? []), c])
  }
  return [...m.entries()]
    .sort(([a], [z]) => (a === '' ? 1 : z === '' ? -1 : ordreSalle(a) - ordreSalle(z) || a.localeCompare(z, 'fr', { numeric: true })))
    .map(([salle, cams]) => ({ salle: salle ? nomSalle(salle) : 'Sans salle', cameras: cams.sort((a, z) => a.display_name.localeCompare(z.display_name, 'fr', { numeric: true })) }))
}
